import { el } from '../dom.js';
import { LIGHTING } from '../../core/SimConfig.js';

/**
 * In-game pause menu — a centered card over a full-screen backdrop. The backdrop
 * captures pointer events so clicking outside the card can't re-engage pointer
 * lock on the canvas. Offers: resume, change lighting, settings overlay,
 * controls overlay, and exit back to the start screen.
 */
export default class PauseMenu {
  constructor({ onResume, onRestart, onEndMission, onExit, onLightingChange, onOpenOverlay }) {
    this.onResume = onResume;
    this.onRestart = onRestart;
    this.onEndMission = onEndMission;
    this.onExit = onExit;
    this.onLightingChange = onLightingChange;
    this.onOpenOverlay = onOpenOverlay;

    this.el = el('div', 'screen pause-screen');
    this.el.appendChild(el('div', 'pause-backdrop'));

    const card = el('div', 'pause-card');
    card.appendChild(el('div', 'pause-title', 'MISSION PAUSED'));

    const resume = el('button', 'pause-btn pause-btn--primary', 'Resume');
    resume.type = 'button';
    resume.addEventListener('click', () => this.onResume?.());
    card.appendChild(resume);

    // End Mission — only meaningful once the mission has been completed and the
    // player chose to keep flying; revealed by setEndMissionAvailable().
    this._endMission = el('button', 'pause-btn pause-btn--end is-hidden', 'End Mission');
    this._endMission.type = 'button';
    this._endMission.addEventListener('click', () => this.onEndMission?.());
    card.appendChild(this._endMission);

    const restart = el('button', 'pause-btn', 'Restart Mission');
    restart.type = 'button';
    restart.addEventListener('click', () => this.onRestart?.());
    card.appendChild(restart);

    card.appendChild(el('div', 'pause-label', 'Lighting'));
    const lightingRow = el('div', 'btn-group');
    card.appendChild(lightingRow);
    this._lightingGroup = lightingRow;
    this._lightingBtns = Object.entries(LIGHTING).map(([key, cfg]) => {
      const b = el('button', 'seg-btn', cfg.name);
      b.type = 'button';
      b._value = key;
      b.addEventListener('click', () => this.onLightingChange?.(key));
      lightingRow.appendChild(b);
      return b;
    });
    this._lightingNote = el('div', 'pause-note is-hidden');
    card.appendChild(this._lightingNote);

    const settings = el('button', 'pause-btn', 'Settings');
    settings.type = 'button';
    settings.addEventListener('click', () => this.onOpenOverlay?.('settings'));
    card.appendChild(settings);

    const controls = el('button', 'pause-btn', 'Controls');
    controls.type = 'button';
    controls.addEventListener('click', () => this.onOpenOverlay?.('controls'));
    card.appendChild(controls);

    const exit = el('button', 'pause-btn pause-btn--danger', 'Exit to Menu');
    exit.type = 'button';
    exit.addEventListener('click', () => this.onExit?.());
    card.appendChild(exit);

    this.el.appendChild(card);
  }

  /** Highlight the active lighting button to match the current scene lighting. */
  syncLighting(key) {
    this._lightingBtns.forEach((b) => b.classList.toggle('is-active', b._value === key));
  }

  /** Lock the lighting picker in combat — the scenario's time-of-day is fixed
   *  (it factors into the night-ops score bonus) and can't be changed mid-mission. */
  setLightingLocked(locked) {
    this._lightingGroup.classList.toggle('is-disabled', locked);
    this._lightingNote.classList.toggle('is-hidden', !locked);
    if (locked) this._lightingNote.textContent = 'Lighting is locked during combat missions.';
  }

  /** Show/hide the End Mission button (mission completed + resumed → available). */
  setEndMissionAvailable(v) {
    this._endMission.classList.toggle('is-hidden', !v);
  }

  setVisible(v) { this.el.classList.toggle('is-hidden', !v); }
}
